import {
  isAgentBusy,
  isTerminalInvestigationStatus,
} from "#src/view-models/investigations";
import {
  useGetList,
  useMutationWrapper,
  type UseMutationCallbackType,
} from "#src/components/hooks/adapters/adapterUtils";
import {
  captureMatchingQueries,
  restoreMatchingQueries,
  type CachedQuerySnapshotType,
} from "#src/components/hooks/utils/optimisticUpdateUtils";
import {
  type ApprovalDecisionType,
  InvestigationAdapter,
  InvestigationAdapterQueryKeys,
  InvestigationMessageKind,
  InvestigationMessageRole,
  InvestigationStatus,
  Resources,
  SortDirection,
  type CreateInvestigationBodyType,
  type CreateInvestigationMessageBodyType,
  type GetListResponseType,
  type InvestigationDetailType,
  type InvestigationGetListFiltersType,
  type InvestigationMessageType,
  type InvestigationType,
} from "@validereinc/domain";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";
import { useToast } from "@validereinc/common-components";
import { useState } from "react";

const POLLING_INTERVAL_MS = 2_500;
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_MESSAGES_PAGE_SIZE = 20;

type SubmitInvestigationApprovalBodyType = {
  decision: ApprovalDecisionType;
  content?: string;
};

type InvestigationMessageMutationContextType = {
  previousDetail: InvestigationDetailType | undefined;
  previousMessagesQueries: Array<
    CachedQuerySnapshotType<
      InfiniteData<GetListResponseType<InvestigationMessageType>>
    >
  >;
  previousListQueries: Array<
    CachedQuerySnapshotType<
      | GetListResponseType<InvestigationType>
      | InfiniteData<GetListResponseType<InvestigationType>>
    >
  >;
};

const getInvestigationTurnStartedStatusPatch = (): Pick<
  InvestigationType,
  "status" | "progress" | "error" | "updated_at"
> => ({
  status: InvestigationStatus.PENDING,
  progress: [],
  error: null,
  updated_at: new Date().toISOString(),
});

/**
 * After a follow-up or approval, the API flips a terminal investigation back to
 * `pending` before the worker runs. Optimistically mirror that in the detail
 * cache so polling and the progress UI resume immediately instead of waiting
 * for invalidation/refetch while `refetchInterval` is still off.
 */
export const markInvestigationAwaitingAgentInCache = (
  queryClient: QueryClient,
  investigationId: string
) => {
  const queryKey = InvestigationAdapterQueryKeys.getOne({
    id: investigationId,
  });
  const cachedInvestigation =
    queryClient.getQueryData<InvestigationDetailType>(queryKey);

  if (!cachedInvestigation) {
    return;
  }

  queryClient.setQueryData<InvestigationDetailType>(queryKey, {
    ...cachedInvestigation,
    ...getInvestigationTurnStartedStatusPatch(),
  });
};

/**
 * Mirrors a new investigation turn in paginated list caches (history panel,
 * investigations table) so status pills update without waiting for refetch.
 */
export const updateInvestigationInListCaches = (
  queryClient: QueryClient,
  investigationId: string,
  patch: Partial<InvestigationType>
) => {
  queryClient.setQueriesData<
    | GetListResponseType<InvestigationType>
    | InfiniteData<GetListResponseType<InvestigationType>>
  >({ queryKey: [Resources.INVESTIGATION, "getList"], exact: false }, (old) => {
    if (!old) {
      return old;
    }

    const mapInvestigation = (investigation: InvestigationType) =>
      investigation.id === investigationId
        ? { ...investigation, ...patch }
        : investigation;

    if ("pages" in old) {
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          data: page.data.map(mapInvestigation),
        })),
      };
    }

    return {
      ...old,
      data: old.data.map(mapInvestigation),
    };
  });
};

/**
 * Prepends the outgoing follow-up to the newest messages page so the user turn
 * and progress ticker appear together instead of waiting on invalidation.
 */
export const prependOptimisticUserMessageInCache = (
  queryClient: QueryClient,
  investigationId: string,
  body: Extract<CreateInvestigationMessageBodyType, { kind: "message" }>
) => {
  const detail = queryClient.getQueryData<InvestigationDetailType>(
    InvestigationAdapterQueryKeys.getOne({ id: investigationId })
  );
  const actorId = detail?.user_id ?? "00000000-0000-4000-8000-000000000000";
  const timestamp = new Date().toISOString();
  const optimisticMessage: InvestigationMessageType = {
    id: crypto.randomUUID(),
    investigation_id: investigationId,
    role: InvestigationMessageRole.USER,
    kind: InvestigationMessageKind.MESSAGE,
    content: body.content,
    metadata: body.metadata ?? null,
    created_at: timestamp,
    updated_at: timestamp,
    created_by: actorId,
    updated_by: actorId,
  };

  queryClient.setQueriesData<
    InfiniteData<GetListResponseType<InvestigationMessageType>>
  >(
    {
      queryKey: [
        Resources.INVESTIGATION,
        "messages",
        "getList",
        investigationId,
      ],
      exact: false,
    },
    (old) => {
      if (!old?.pages.length) {
        return old;
      }

      const [firstPage, ...remainingPages] = old.pages;

      return {
        ...old,
        pages: [
          {
            ...firstPage,
            data: [optimisticMessage, ...firstPage.data],
            total_entries: firstPage.total_entries + 1,
          },
          ...remainingPages,
        ],
      };
    }
  );
};

const applyInvestigationTurnStartedOptimisticUpdates = (
  queryClient: QueryClient,
  investigationId: string,
  body: CreateInvestigationMessageBodyType
) => {
  const statusPatch = getInvestigationTurnStartedStatusPatch();

  markInvestigationAwaitingAgentInCache(queryClient, investigationId);
  updateInvestigationInListCaches(queryClient, investigationId, statusPatch);

  if (body.kind === "message") {
    prependOptimisticUserMessageInCache(queryClient, investigationId, body);
  }
};

const beginInvestigationMessageMutation = async (
  queryClient: QueryClient,
  investigationId: string,
  body: CreateInvestigationMessageBodyType
): Promise<InvestigationMessageMutationContextType> => {
  await queryClient.cancelQueries({
    queryKey: InvestigationAdapterQueryKeys.getOne({ id: investigationId }),
  });
  await queryClient.cancelQueries({
    queryKey: [Resources.INVESTIGATION, "messages", "getList", investigationId],
  });

  const context: InvestigationMessageMutationContextType = {
    previousDetail: queryClient.getQueryData<InvestigationDetailType>(
      InvestigationAdapterQueryKeys.getOne({ id: investigationId })
    ),
    previousMessagesQueries: captureMatchingQueries<
      InfiniteData<GetListResponseType<InvestigationMessageType>>
    >(queryClient, [
      Resources.INVESTIGATION,
      "messages",
      "getList",
      investigationId,
    ]),
    previousListQueries: captureMatchingQueries<
      | GetListResponseType<InvestigationType>
      | InfiniteData<GetListResponseType<InvestigationType>>
    >(queryClient, [Resources.INVESTIGATION, "getList"]),
  };

  applyInvestigationTurnStartedOptimisticUpdates(
    queryClient,
    investigationId,
    body
  );

  return context;
};

const rollbackInvestigationMessageMutation = (
  queryClient: QueryClient,
  investigationId: string,
  context: InvestigationMessageMutationContextType | undefined
) => {
  if (!context) {
    return;
  }

  queryClient.setQueryData(
    InvestigationAdapterQueryKeys.getOne({ id: investigationId }),
    context.previousDetail
  );
  restoreMatchingQueries(queryClient, context.previousMessagesQueries);
  restoreMatchingQueries(queryClient, context.previousListQueries);
};

const useInvalidateInvestigationDetailAndMessages = (id: string) => {
  const queryClient = useQueryClient();
  const [isInvalidating, setIsInvalidating] = useState(false);

  const invalidateDetailAndMessages = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: InvestigationAdapterQueryKeys.getOne({ id }),
      }),
      queryClient.invalidateQueries({
        queryKey: [Resources.INVESTIGATION, "messages", "getList", id],
      }),
      queryClient.invalidateQueries({
        queryKey: [Resources.INVESTIGATION, "getList"],
      }),
    ]);

  const invalidateDetailAndMessagesWithLoading = async () => {
    setIsInvalidating(true);
    try {
      await invalidateDetailAndMessages();
    } finally {
      setIsInvalidating(false);
    }
  };

  return {
    invalidateDetailAndMessages,
    invalidateDetailAndMessagesWithLoading,
    isInvalidating,
  };
};

/**
 * Detect the 409 the API returns when a follow-up arrives while the agent is
 * mid-turn. We don't have a typed error class from the rest client yet, so we
 * sniff the shape: `{ response: { status: 409, data: { code: "investigation_in_progress" } } }`.
 */
export const isInvestigationInProgressError = (err: unknown): boolean => {
  if (typeof err !== "object" || err === null) return false;
  const response = (err as { response?: unknown }).response;
  if (typeof response !== "object" || response === null) return false;
  const { status, data } = response as { status?: unknown; data?: unknown };
  if (status !== 409) return false;
  if (typeof data !== "object" || data === null) return true;
  const code = (data as { code?: unknown }).code;
  return code === undefined || code === "investigation_in_progress";
};

type InvestigationMessageMutationToastType = ReturnType<
  typeof useToast
>["toast"];

/**
 * Shared `onError` for investigation `messages.createOne` mutations (follow-up
 * and approval). On 409 `investigation_in_progress`, show a warning and refresh
 * stale detail/messages cache so the next poll converges.
 */
const createInvestigationMessageMutationOnError =
  (
    invalidateDetailAndMessages: () => Promise<unknown[]>,
    toast: InvestigationMessageMutationToastType,
    genericFailureDescription: string
  ) =>
  (err: unknown) => {
    const isInProgress = isInvestigationInProgressError(err);

    toast.push({
      intent: isInProgress ? "warning" : "error",
      description: isInProgress
        ? "An investigation turn is still running. Please try again shortly."
        : genericFailureDescription,
    });

    if (isInProgress) {
      void invalidateDetailAndMessages();
    }

    console.error(err);
  };

export const useInvestigation = (id?: string) => {
  const queryClient = useQueryClient();

  return useQuery<InvestigationDetailType>({
    queryKey: InvestigationAdapterQueryKeys.getOne({ id: id ?? "" }),
    queryFn: async () => {
      const { data } = await InvestigationAdapter.getOne({ id: id! });
      // The cache still holds the previous detail at this point; react-query
      // only writes `data` after `queryFn` resolves. If we observe a busy →
      // terminal transition here, kick a one-shot messages refetch so the
      // final agent reply shows up without a manual reload (the messages
      // poll is about to stop on the very next tick).
      const previous = queryClient.getQueryData<InvestigationDetailType>(
        InvestigationAdapterQueryKeys.getOne({ id: id! })
      );
      if (
        previous &&
        isAgentBusy(previous.status) &&
        isTerminalInvestigationStatus(data.status)
      ) {
        updateInvestigationInListCaches(queryClient, id!, {
          status: data.status,
          progress: data.progress,
          error: data.error,
          updated_at: data.updated_at,
          report: data.report,
        });
        void queryClient.invalidateQueries({
          queryKey: [Resources.INVESTIGATION, "messages", "getList", id!],
        });
      }
      return data;
    },
    enabled: Boolean(id),
    refetchIntervalInBackground: true,
    refetchInterval: (data) =>
      data && !isTerminalInvestigationStatus(data.status)
        ? POLLING_INTERVAL_MS
        : false,
  });
};

export const useListInvestigations = useGetList(
  InvestigationAdapter.getList,
  Resources.INVESTIGATION
);

export const useUpdateInvestigation = (
  options: UseMutationCallbackType<
    Awaited<ReturnType<typeof InvestigationAdapter.updateOne>>
  > = {}
) =>
  useMutationWrapper(
    InvestigationAdapter.updateOne,
    [Resources.INVESTIGATION],
    {
      successMessage: "Successfully updated Investigation",
      errorMessage: "Failed to update Investigation",
      ...options,
    }
  );

export const useInvestigationsInfiniteList = ({
  pageSize = DEFAULT_PAGE_SIZE,
  filters,
}: {
  pageSize?: number;
  filters?: InvestigationGetListFiltersType;
} = {}) =>
  useInfiniteQuery({
    queryKey: [
      ...InvestigationAdapterQueryKeys.getList({ pageSize, filters }),
      "infinite",
    ] as const,
    queryFn: async ({ pageParam = 1 }) =>
      InvestigationAdapter.getList({
        page: pageParam as number,
        pageSize,
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
        filters,
      }),
    keepPreviousData: true,
    getNextPageParam: (lastPage) =>
      lastPage.page_number < lastPage.total_pages
        ? lastPage.page_number + 1
        : undefined,
    refetchIntervalInBackground: true,
    refetchInterval: (data) => {
      const hasNonTerminalInvestigation = (data?.pages ?? []).some((page) =>
        page.data.some(
          (investigation) =>
            !isTerminalInvestigationStatus(investigation.status)
        )
      );

      return hasNonTerminalInvestigation ? POLLING_INTERVAL_MS : false;
    },
  });

export const useInvestigationMessages = (
  id: string | undefined,
  { pageSize = DEFAULT_MESSAGES_PAGE_SIZE }: { pageSize?: number } = {}
) => {
  const { data: investigation } = useInvestigation(id);

  const shouldPollMessages =
    Boolean(investigation) &&
    !isTerminalInvestigationStatus(investigation!.status);

  return useInfiniteQuery({
    queryKey: InvestigationAdapterQueryKeys.messages.getList({
      meta: { id: id ?? "" },
      pageSize,
    }),
    queryFn: ({ pageParam = 1 }) =>
      InvestigationAdapter.messages.getList({
        meta: { id: id! },
        page: pageParam as number,
        pageSize,
      }),
    enabled: Boolean(id),
    // Subscribe to `useInvestigation` above so this hook re-renders when the
    // detail status flips terminal → pending after a follow-up. Reading
    // `getQueryData` inside `refetchInterval` alone does not re-arm polling.
    refetchIntervalInBackground: true,
    refetchInterval: shouldPollMessages ? POLLING_INTERVAL_MS : false,
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((sum, p) => sum + p.data.length, 0);
      return fetched < lastPage.total_entries ? allPages.length + 1 : undefined;
    },
  });
};

export const useCreateInvestigation = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateInvestigationBodyType) =>
      InvestigationAdapter.createOne({ data: body }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [Resources.INVESTIGATION],
      });
    },
    onError: (err) => {
      toast.push({
        intent: "error",
        description: "Failed to start the investigation. Please try again.",
      });
      console.error(err);
    },
  });
};

export const usePostInvestigationMessage = (id: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const {
    invalidateDetailAndMessages,
    invalidateDetailAndMessagesWithLoading,
    isInvalidating,
  } = useInvalidateInvestigationDetailAndMessages(id);

  // On a 409 race the cache is stale: the detail page still thinks the agent
  // is idle. Invalidate both the detail and the message list so the next poll
  // pulls the fresh status and the now-finished agent reply.
  const mutation = useMutation({
    mutationFn: (body: CreateInvestigationMessageBodyType) =>
      InvestigationAdapter.messages.createOne({
        data: body,
        meta: { id },
      }),
    onMutate: (body) =>
      beginInvestigationMessageMutation(queryClient, id, body),
    onSuccess: invalidateDetailAndMessagesWithLoading,
    onError: (err, _body, context) => {
      rollbackInvestigationMessageMutation(queryClient, id, context);
      createInvestigationMessageMutationOnError(
        invalidateDetailAndMessages,
        toast,
        "Failed to send the follow-up. Please try again."
      )(err);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isLoading || isInvalidating,
  };
};

export const useSubmitInvestigationApproval = (id: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const {
    invalidateDetailAndMessages,
    invalidateDetailAndMessagesWithLoading,
    isInvalidating,
  } = useInvalidateInvestigationDetailAndMessages(id);

  const mutation = useMutation({
    mutationFn: (body: SubmitInvestigationApprovalBodyType) => {
      const messageBody: CreateInvestigationMessageBodyType = {
        kind: "approval",
        decision: body.decision,
      };
      if (body.content) {
        messageBody.content = body.content;
      }

      return InvestigationAdapter.messages.createOne({
        data: messageBody,
        meta: { id },
      });
    },
    onMutate: (body) => {
      const messageBody: CreateInvestigationMessageBodyType = {
        kind: "approval",
        decision: body.decision,
      };
      if (body.content) {
        messageBody.content = body.content;
      }

      return beginInvestigationMessageMutation(queryClient, id, messageBody);
    },
    onSuccess: invalidateDetailAndMessagesWithLoading,
    onError: (err, _body, context) => {
      rollbackInvestigationMessageMutation(queryClient, id, context);
      createInvestigationMessageMutationOnError(
        invalidateDetailAndMessages,
        toast,
        "Failed to submit approval. Please try again."
      )(err);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isLoading || isInvalidating,
  };
};
